import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { BillItemPendingModel } from '@data-layer/models/payments';
import { enumBillItemProductCode } from '@data-layer/enums/payments';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { UtilsHelper } from '@helpers/utils-helper';

@Component({
  selector: 'app-payment-breakdown',
  templateUrl: './payment-breakdown.component.html',
  styleUrls: ['./payment-breakdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentBreakdownComponent {
  @Input() public pendingBills: BillItemPendingModel[] = [];
  @Input() public totalText: string = 'LABELS.TOTAL_PAYMENT';
  @Input() public notes: string[] = [];
  @Input() public isMobileView: boolean;
  @Input() public isCollapsed: boolean;
  @Input() public isCollapsable: boolean;
  @Input() public isHideMonthlyPayment: boolean;

  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;

  public isDiscount(item: BillItemPendingModel): boolean {
    return item.productCode === enumBillItemProductCode.DISCOUNT;
  }

  public get totalSum(): number {
    return (
      (this.pendingBills || [])
        .filter((item: BillItemPendingModel) => (this.isHideMonthlyPayment ? item?.paymentType !== enumCompanyPaymentType.MONTHLY : true))
        .reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price * curr.quantity, 0) || 0
    );
  }

  public get normalJoinedItems(): BillItemPendingModel[] {
    return this.joinedItems.filter((bill) => !this.isDiscount(bill));
  }

  public get discountJoinedItems(): BillItemPendingModel[] {
    return this.joinedItems.filter((bill) => this.isDiscount(bill));
  }

  public get discountsTotalSum(): number {
    return this.discountJoinedItems.reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price, 0);
  }

  private getProductsExcludedItems(items: BillItemPendingModel[]): BillItemPendingModel[] {
    const productsExcludedItems: BillItemPendingModel[] = (items || []).filter(
      (paymentItem: BillItemPendingModel) =>
        ![enumBillItemProductCode.COLLECTION_FEE].includes(paymentItem.productCode) &&
        ![enumBillItemProductCode.DISCOUNT].includes(paymentItem.productCode) &&
        (this.isHideMonthlyPayment ? paymentItem.paymentType === enumCompanyPaymentType.INSTANT : true),
    );
    return productsExcludedItems;
  }

  private getProductSpecificItems(items: BillItemPendingModel[], productCode: enumBillItemProductCode): BillItemPendingModel[] {
    const productSpecificItems: BillItemPendingModel[] =
      (items || []).filter(
        (paymentItem: BillItemPendingModel) =>
          paymentItem.productCode === productCode &&
          (this.isHideMonthlyPayment ? paymentItem.paymentType === enumCompanyPaymentType.INSTANT : true),
      ) || [];

    return productSpecificItems;
  }

  private get joinedItems(): BillItemPendingModel[] {
    const productsExcludedItems: BillItemPendingModel[] = this.getProductsExcludedItems(this.pendingBills);
    const productsExcludedItemsCodes: enumBillItemProductCode[] = UtilsHelper.uniq<enumBillItemProductCode>(
      productsExcludedItems.map((item: BillItemPendingModel) => item.productCode),
    );

    const collectionFeeItems: BillItemPendingModel[] = this.getProductSpecificItems(
      this.pendingBills,
      enumBillItemProductCode.COLLECTION_FEE,
    );
    const discountItems: BillItemPendingModel[] = this.getProductSpecificItems(this.pendingBills, enumBillItemProductCode.DISCOUNT);

    const result: BillItemPendingModel[] = [];

    productsExcludedItemsCodes.forEach((item: enumBillItemProductCode) => {
      const productItems: BillItemPendingModel[] =
        (this.pendingBills || []).filter(
          (paymentItem: BillItemPendingModel) =>
            paymentItem.productCode === item &&
            (this.isHideMonthlyPayment ? paymentItem.paymentType === enumCompanyPaymentType.INSTANT : true),
        ) || [];

      result.push({
        ...productItems[0],
        quantity: productItems.reduce((acc: number, curr: BillItemPendingModel) => acc + curr.quantity, 0),
        price: productItems[0]?.price,
        paymentType: enumCompanyPaymentType.INSTANT,
      });
    });

    if (collectionFeeItems?.length) {
      result.push({
        ...collectionFeeItems[0],
        quantity: 1,
        price: collectionFeeItems.reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price, 0),
        paymentType: enumCompanyPaymentType.INSTANT,
      });
    }

    if (discountItems?.length) {
      result.push({
        ...discountItems[0],
        quantity: discountItems.reduce((acc: number, curr: BillItemPendingModel) => acc + curr.quantity, 0),
        price: discountItems.reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price, 0),
        paymentType: enumCompanyPaymentType.INSTANT,
      });
    }

    return result || [];
  }
}
